import { Mic, Pause, Play, Send, Square, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from '../../components/toasts';
import { Button } from '../../components/ui';
import { formatDuration } from '../../lib/format';

/** Formato gravado: OGG/Opus (o do WhatsApp) quando o navegador suporta; senão WebM/Opus. */
function pickMimeType(): string {
  const options = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  return options.find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) ?? '';
}

const MAX_SECONDS = 15 * 60;

type State = 'idle' | 'recording' | 'paused' | 'review' | 'sending';

/**
 * Gravação de áudio no navegador (vai ao cliente como mensagem de voz), como no WhatsApp: pausar e
 * continuar, parar para ouvir antes de enviar, ou enviar direto. Exige microfone e um endereço
 * seguro (https ou localhost): em http://IP o navegador bloqueia o microfone.
 */
export function AudioRecorder({ disabled, onRecorded }: { disabled?: boolean; onRecorded: (file: File) => Promise<void> | void }) {
  const [state, setState] = useState<State>('idle');
  const [seconds, setSeconds] = useState(0);
  const [review, setReview] = useState<{ file: File; url: string } | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  /** O que fazer quando a gravação parar. */
  const onStop = useRef<'discard' | 'send' | 'review'>('discard');
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimer = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };
  const startTimer = () => {
    stopTimer();
    timer.current = setInterval(() => {
      setSeconds((s) => {
        // Limite: para e abre a revisão.
        if (s + 1 >= MAX_SECONDS && recorder.current?.state === 'recording') {
          onStop.current = 'review';
          recorder.current.stop();
        }
        return s + 1;
      });
    }, 1000);
  };
  const stopTracks = () => recorder.current?.stream.getTracks().forEach((t) => t.stop());

  useEffect(
    () => () => {
      onStop.current = 'discard';
      if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
      stopTracks();
      stopTimer();
    },
    [],
  );
  // Libera o áudio da revisão ao trocar ou sair.
  useEffect(() => () => void (review && URL.revokeObjectURL(review.url)), [review]);

  const send = async (file: File) => {
    setState('sending');
    try {
      await onRecorded(file);
    } finally {
      setReview(null);
      setState('idle');
    }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast('Este navegador não permite gravar áudio aqui. O microfone exige endereço https ou localhost.', 'error');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast('Sem acesso ao microfone: libere a permissão no navegador.', 'error');
      return;
    }
    const mimeType = pickMimeType();
    const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunks.current = [];
    onStop.current = 'discard';
    rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    rec.onstop = () => {
      stopTracks();
      stopTimer();
      const action = onStop.current;
      if (action === 'discard' || !chunks.current.length) {
        setState('idle');
        return;
      }
      const type = (rec.mimeType || mimeType || 'audio/webm').split(';')[0];
      const ext = type.includes('ogg') ? 'ogg' : type.includes('mp4') ? 'm4a' : 'webm';
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const file = new File(chunks.current, `audio-${stamp}.${ext}`, { type });
      if (action === 'send') {
        void send(file);
      } else {
        setReview({ file, url: URL.createObjectURL(file) });
        setState('review');
      }
    };
    recorder.current = rec;
    rec.start(250);
    setSeconds(0);
    setState('recording');
    startTimer();
  };

  const togglePause = () => {
    const rec = recorder.current;
    if (!rec) return;
    if (rec.state === 'recording') {
      rec.pause();
      stopTimer();
      setState('paused');
    } else if (rec.state === 'paused') {
      rec.resume();
      startTimer();
      setState('recording');
    }
  };

  const stop = (action: 'discard' | 'send' | 'review') => {
    onStop.current = action;
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
  };

  const discardReview = () => {
    setReview(null);
    setState('idle');
  };

  if (state === 'idle') {
    return (
      <Button variant="ghost" disabled={disabled} onClick={() => void start()} aria-label="Gravar áudio" title="Gravar áudio">
        <Mic className="size-4" />
      </Button>
    );
  }

  if ((state === 'review' || state === 'sending') && review) {
    return (
      <div className="flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1">
        <Button size="sm" variant="ghost" onClick={discardReview} disabled={state === 'sending'} aria-label="Descartar áudio" title="Descartar">
          <Trash2 className="size-3.5" />
        </Button>
        <ReviewPlayer url={review.url} seconds={seconds} />
        <Button size="sm" variant="primary" onClick={() => void send(review.file)} loading={state === 'sending'} aria-label="Enviar áudio" title="Enviar áudio">
          <Send className="size-3.5" />
        </Button>
      </div>
    );
  }

  const paused = state === 'paused';
  return (
    <div className="flex items-center gap-1 rounded-md border border-danger/40 bg-danger/5 px-2 py-1">
      <Button size="sm" variant="ghost" onClick={() => stop('discard')} disabled={state === 'sending'} aria-label="Descartar áudio" title="Descartar">
        <Trash2 className="size-3.5" />
      </Button>
      <span className={paused ? 'size-2 rounded-full bg-fg-soft' : 'size-2 animate-pulse rounded-full bg-danger'} />
      <span className="w-12 font-mono text-xs" title={paused ? 'Pausado' : 'Gravando'}>
        {formatDuration(seconds)}
      </span>
      <Button size="sm" variant="ghost" onClick={togglePause} disabled={state === 'sending'} aria-label={paused ? 'Continuar gravação' : 'Pausar gravação'} title={paused ? 'Continuar' : 'Pausar'}>
        {paused ? <Mic className="size-3.5 text-danger" /> : <Pause className="size-3.5" />}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => stop('review')} disabled={state === 'sending'} aria-label="Parar e ouvir" title="Parar e ouvir antes de enviar">
        <Square className="size-3.5" />
      </Button>
      <Button size="sm" variant="primary" onClick={() => stop('send')} loading={state === 'sending'} aria-label="Enviar áudio" title="Enviar agora">
        <Send className="size-3.5" />
      </Button>
    </div>
  );
}

/**
 * Player compacto da revisão. O Chrome grava WebM sem a duração no arquivo: o tempo total vem da
 * contagem da gravação, e a barra de progresso usa esse total.
 */
function ReviewPlayer({ url, seconds }: { url: string; seconds: number }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const total = Math.max(seconds, 1);

  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  };
  const seek = (value: number) => {
    const el = audio.current;
    if (!el) return;
    el.currentTime = value;
    setPosition(value);
  };

  return (
    <div className="flex items-center gap-1.5">
      <audio
        ref={audio}
        src={url}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPosition(0);
        }}
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
      />
      <Button size="sm" variant="ghost" onClick={toggle} aria-label={playing ? 'Pausar' : 'Ouvir'} title={playing ? 'Pausar' : 'Ouvir'}>
        {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
      </Button>
      <input
        type="range"
        min={0}
        max={total}
        step={0.1}
        value={Math.min(position, total)}
        onChange={(e) => seek(Number(e.target.value))}
        className="w-28 accent-[var(--accent)]"
        aria-label="Posição do áudio"
      />
      <span className="w-20 font-mono text-[11px] text-fg-soft">
        {formatDuration(Math.floor(position))} / {formatDuration(seconds)}
      </span>
    </div>
  );
}
